import { Phone, MessageCircle } from "lucide-react";
import { Reveal } from "./Reveal";
import { PHOTOS } from "../images";
import { useEnquiry } from "../hooks/useEnquiry";

const inputCls =
  "w-full rounded-none border border-brand-champagne bg-white px-4 py-3 text-sm text-brand-licorice placeholder:text-brand-licorice/40 outline-none transition-colors focus:border-brand-brunswick";

const Field = ({ label, children, htmlFor }) => (
  <div>
    <label htmlFor={htmlFor} className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-licorice/60">
      {label}
    </label>
    {children}
  </div>
);

export const Enquiry = () => {
  const { form, set, submit, sending } = useEnquiry("Dune Villas");

  return (
    <section id="enquire" data-testid="enquiry-section" className="grain relative overflow-hidden bg-brand-licorice py-20 lg:py-36">
      {PHOTOS.escape && (
        <>
          <img
            src={PHOTOS.escape}
            alt="The pool at Area 83 at blue hour"
            loading="lazy"
            className="drift-zoom absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-brand-licorice/80" />
        </>
      )}
      <div className="relative z-[2] mx-auto grid max-w-7xl items-center gap-14 px-6 sm:px-8 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <p className="mb-5 text-[11px] font-semibold uppercase tracking-[0.28em] text-brand-champagne">Enquire</p>
          <h2 className="font-serif text-4xl font-light leading-[1.08] tracking-tight text-brand-ivory sm:text-5xl lg:text-6xl">
            Make the weekend feel <span className="italic text-brand-champagne">further away.</span>
          </h2>
          <p className="mt-7 max-w-md text-[15px] leading-relaxed text-brand-ivory/75 sm:text-lg">
            Tell us your dates and group size. We&rsquo;ll help you choose the right Dune Villa.
          </p>
          <div className="mt-10 flex flex-col gap-4 sm:flex-row">
            <a
              href="tel:+918046396067"
              data-testid="enquiry-call-cta"
              className="inline-flex items-center justify-center gap-3 border border-brand-ivory/40 px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.16em] text-brand-ivory transition-colors hover:bg-brand-ivory/10"
            >
              <Phone size={16} strokeWidth={1.75} /> Call us
            </a>
            <a
              href="https://wa.me/918884608383?text=Hi%2C%20I%27d%20like%20to%20enquire%20about%20a%20stay%20at%20Dune%20Villas."
              target="_blank"
              rel="noopener noreferrer"
              data-testid="enquiry-whatsapp-cta"
              className="inline-flex items-center justify-center gap-3 bg-brand-brunswick px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.16em] text-brand-ivory transition-colors hover:bg-brand-moss"
            >
              <MessageCircle size={16} strokeWidth={1.75} /> WhatsApp
            </a>
          </div>
          <p className="mt-6 text-sm text-brand-ivory/50">Reservations: +91 80463 96067 · Support: +91 88846 08383 (10 AM to 8 PM)</p>
        </Reveal>

        <Reveal delay={0.12}>
          <form
            onSubmit={submit}
            data-testid="enquiry-form"
            className="bg-brand-ivory p-7 sm:p-10"
            noValidate={false}
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Name" htmlFor="enquiry-name">
                <input id="enquiry-name" data-testid="enquiry-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
              </Field>
              <Field label="Phone" htmlFor="enquiry-phone">
                <input id="enquiry-phone" data-testid="enquiry-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="+91" className={inputCls} />
              </Field>
              <Field label="Email" htmlFor="enquiry-email">
                <input id="enquiry-email" data-testid="enquiry-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
              </Field>
              <Field label="Preferred dates" htmlFor="enquiry-dates">
                <input id="enquiry-dates" data-testid="enquiry-dates-input" value={form.dates} onChange={set("dates")} placeholder="e.g. 12 to 14 September" className={inputCls} />
              </Field>
              <Field label="Number of guests" htmlFor="enquiry-guests">
                <input id="enquiry-guests" data-testid="enquiry-guests-input" type="number" min="1" value={form.guests} onChange={set("guests")} placeholder="e.g. 12" className={inputCls} />
              </Field>
              <Field label="Occasion" htmlFor="enquiry-occasion">
                <select id="enquiry-occasion" data-testid="enquiry-occasion-select" value={form.occasion} onChange={set("occasion")} className={inputCls}>
                  <option value="">Select an occasion</option>
                  <option>Family getaway</option>
                  <option>Friends&rsquo; trip</option>
                  <option>Birthday</option>
                  <option>Reunion</option>
                  <option>Intimate celebration</option>
                  <option>Just a weekend away</option>
                </select>
              </Field>
              <div className="sm:col-span-2">
                <Field label="Message" htmlFor="enquiry-message">
                  <textarea id="enquiry-message" data-testid="enquiry-message-input" rows={4} value={form.message} onChange={set("message")} placeholder="Anything we should know about your group or plans?" className={`${inputCls} resize-none`} />
                </Field>
              </div>
            </div>
            <button
              type="submit"
              data-testid="enquiry-submit-button"
              disabled={sending}
              className="mt-7 w-full bg-brand-rust py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-brand-ivory transition-colors hover:bg-brand-sepia disabled:opacity-60"
            >
              {sending ? "Sending…" : "Send enquiry"}
            </button>
            <p className="mt-4 text-xs text-brand-licorice/50">
              We usually respond within a few hours during working hours.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
